import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AxiosError, AxiosResponse } from 'axios'
import axios from 'axios'
import type { GenericQueryOptions } from '.'
import type {
  AcquisitionNumber,
  EditOrganizationUnit,
  EditUser,
  InsurerOrganizationUnit,
  InsurerOrganizationUnitDiscount,
  LdapUser,
  OrganizationUnit,
  OrganizationUnitFleetNumber,
  OrganizationUnitGrid,
  OrganizationUnitPagedQuery,
  SetOrganizationUnitInsurerValid,
  TransferOrganizationUnit,
  TransferUser,
  UpdateOrganizationUnitDiscount,
  UserBase,
} from '../dtos'
import type { InsuranceCategoryDiscount } from '../dtos/OrganizationUnit/Discounts/InsuranceCategoryDiscount'
import type { OrganizationUnitProperties } from '../dtos/OrganizationUnit/OrganizationUnitProperties'
import type { ToggleIsActiveUser } from '../dtos/OrganizationUnit/ToggleIsActiveUser'
import type { OrganizationUnitType, Product } from '../enums'
import apiClient from '../Utils/apiClient'
import { returnArray } from '../Utils/reactQueryUtils'

const baseUrl = import.meta.env.VITE_BASE_URL
const apiUrl = import.meta.env.VITE_ORGANIZATION_UNIT_API
const url = `${baseUrl}/${apiUrl}`

const fiveMinutes = 1000 * 60 * 5

export const useGetAll = (search: OrganizationUnitPagedQuery, options?: GenericQueryOptions<OrganizationUnitGrid>) => {
  return useQuery({
    queryKey: ['organizationUnits', search],
    queryFn: () => axios.post<OrganizationUnitGrid>(`${url}/GetAllOrganizationUnits`, search),
    ...options,
  })
}

export const useGetCurrentOrganizationUnit = () => {
  return useQuery<AxiosResponse<OrganizationUnit>, AxiosError>({
    queryKey: ['organizationUnit', 'current'],
    queryFn: () => axios.get<OrganizationUnit>(`${url}/GetCurrent`),
  })
}

export const useGetOrganizationUnitById = (ouId: number) => {
  return useQuery<AxiosResponse<OrganizationUnit>, AxiosError>({
    queryKey: ['organizationUnit', ouId],
    queryFn: () => axios.get<OrganizationUnit>(`${url}/GetById`, { params: { ouId } }),
    staleTime: fiveMinutes
  })
}

export const useGetInsurers = (ouId: number, product?: Product) => {
  return useQuery({
    queryKey: ['organizationUnit', ouId, product, 'insurers'],
    queryFn: () =>
      axios.get<InsurerOrganizationUnit[]>(`${url}/GetOrganizationUnitInsurers`, {
        params: { ouId, product },
      }).then(returnArray),
    staleTime: fiveMinutes
  })
}

export const useGetAvailableInsurers = (ouId: number) => {
  return useQuery({
    queryKey: ['organizationUnit', ouId, 'availableInsurers'],
    queryFn: () =>
      axios.get<{ insurerId: number; insurer: string; ouId: number }[]>(
        `${url}/GetAvailableOrganizationUnitInsurers`,
        { params: { ouId } },
      ),
    staleTime: fiveMinutes
  })
}

export const useGetInsurerDiscounts = (
  ouId: number,
  insurerId: number,
  product: Product,
  options?: GenericQueryOptions<{
    assignedDiscounts: InsurerOrganizationUnitDiscount[]
    availableDiscounts: InsuranceCategoryDiscount[]
  }>,
) => {
  return useQuery({
    queryKey: ['organizationUnit', ouId, product, 'insurers', insurerId],
    queryFn: () =>
      axios.get<{
        assignedDiscounts: InsurerOrganizationUnitDiscount[]
        availableDiscounts: InsuranceCategoryDiscount[]
      }>(`${url}/GetInsurerDiscounts`, {
        params: { ouId, insurerId, product },
      }),
    ...options
  })
}

export const useUpdateDiscount = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (updateDiscount: UpdateOrganizationUnitDiscount) =>
      axios.post(`${url}/UpdateDiscount`, updateDiscount),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [
        'organizationUnit',
        variables.organizationUnitId,
        variables.insuranceCategoryType,
      ] })
    },
  })
}

export const useSetInsurerValid = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (updateDiscount: SetOrganizationUnitInsurerValid) =>
      axios.post(`${url}/SetInsurerValid`, updateDiscount),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [
        'organizationUnit',
        variables.organizationUnitId,
        variables.insuranceCategoryType,
        'insurers',
      ] })
      queryClient.invalidateQueries({ queryKey: ['organizationUnit', variables.organizationUnitId, 'availableInsurers'] })
    }
  })
}

export const useGetOrganizationUnitProperties = (ouId: number) => {
  return useQuery({
    queryKey: ['organizationUnit', ouId, 'properties'],
    queryFn: () =>
      axios.get<OrganizationUnitProperties>(`${url}/GetOrganizationUnitProperties`, {
        params: { ouId },
      }),
    staleTime: fiveMinutes
  })
}

export const useUpdateOrganizationUnit = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (update: EditOrganizationUnit) => axios.post(`${url}/UpdateOrganizationUnit`, update),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['organizationUnit', variables.id] })
    }
  })
}

export const useCreateOrganizationUnit = () => {
  return useMutation({
    mutationFn: (update: EditOrganizationUnit) =>
      axios.post<OrganizationUnit>(`${url}/CreateOrganizationUnit`, update)
  })
}

export const useGetPossibleTypes = (
  ouId?: number | undefined,
  superiorOrganizationUnitId?: number | undefined,
) => {
  return useQuery({
    queryKey: ['organizationUnit', ouId, 'possibleTypes', superiorOrganizationUnitId],
    queryFn: () =>
      axios.get<OrganizationUnitType[]>(`${url}/GetPossibleOrganizationUnitTypes`, {
        params: { ouId, superiorOrganizationUnitId },
      }),
    staleTime: fiveMinutes
  })
}

export const useGetOrganizationFleetNumbers = (
  ouId?: number | undefined,
  options?: GenericQueryOptions<OrganizationUnitFleetNumber[]>,
) => {
  return useQuery({
    queryKey: ['organizationUnit', ouId, 'fleetNumbers'],
    queryFn: () =>
      axios.get<OrganizationUnitFleetNumber[]>(`${url}/GetFleetNumbers`, {
        params: { ouId },
      }),
    ...options
  })
}

export const useGetAcquisitionNumbers = (
  ouId?: number | undefined,
  superiorOrganizationUnitId?: number | undefined,
  options?: GenericQueryOptions<AcquisitionNumber[]>,
) => {
  return useQuery({
    queryKey: ['organizationUnit', ouId, 'acquisitionNumbers', superiorOrganizationUnitId],
    queryFn: () =>
      axios.get<AcquisitionNumber[]>(`${url}/GetAcquisitionNumbers`, {
        params: { ouId, superiorOrganizationUnitId },
      }),
    ...options
  })
}

export const useCreateUser = () => {
  return useMutation({ mutationFn: (create: EditUser) => axios.post<UserBase>(`${url}/CreateUser`, create) })
}

export const useTransferUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (transferUser: TransferUser) => apiClient.post(`${url}/TransferUser`, transferUser),
    onSuccess: (_, variables) => variables.userIds.forEach(userId => queryClient.invalidateQueries({ queryKey: ['user', userId] }))
  })
}

export const useToggleIsActiveUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (transferUser: ToggleIsActiveUser) => apiClient.post(`${url}/ToggleIsActiveUser`, transferUser),
    onSuccess: (_, variables) => variables.userIds.forEach(userId => queryClient.invalidateQueries({ queryKey: ['user', userId] }))
  })
}

export const useTransferOrganizationUnit = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (transfer: TransferOrganizationUnit) =>
      apiClient.post<void>(`${url}/TransferOrganizationUnit`, transfer),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['organizationUnit', variables.organizationUnitId] })
      queryClient.invalidateQueries({ queryKey: ['organizationUnit', variables.targetOrganizationUnitId] })
    }
  })
}

export const useGetLdapUser = () => {
  return useMutation({
    mutationFn: (userName: string) =>
      axios.get<LdapUser>(`${url}/GetLdapUser`, { params: { userName } }),
  })
}

const ouLogoApiUrl = import.meta.env.VITE_OU_LOGO_API
const ouLogoUrl = `${baseUrl}/${ouLogoApiUrl}`

export const useUploadLogo = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (form: { formData: FormData; ouId: number }) =>
      axios.post<string>(`${ouLogoUrl}/UploadLogo`, form.formData, {
        params: { ouId: form.ouId },
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    onSuccess: (_, variables) =>
      queryClient.invalidateQueries({ queryKey: ['organizationUnit', variables.ouId] })
  })
}

export const useDeleteLogo = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (ouId: number) => axios.post(`${ouLogoUrl}/DeleteLogo`, null, { params: { ouId } }),
    onSuccess: (_, ouId) => queryClient.invalidateQueries({ queryKey: ['organizationUnit', ouId] })
  })
}
